import { useState } from 'react';
import { auth } from './firebase';
import {
  linkWithPopup,
  linkWithCredential,
  unlink,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  GithubAuthProvider,
} from 'firebase/auth';

const PROVIDERS = [
  { id: 'google.com', label: 'Google', icon: <GoogleIcon />, factory: () => new GoogleAuthProvider() },
  { id: 'apple.com', label: 'Apple', icon: <AppleIcon />, factory: () => { const p = new OAuthProvider('apple.com'); p.addScope('email'); p.addScope('name'); return p; } },
  { id: 'github.com', label: 'GitHub', icon: <GitHubIcon />, factory: () => new GithubAuthProvider() },
];

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  padding: 24,
  marginBottom: 20,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

export default function LinkedProvidersSection() {
  const [linked, setLinked] = useState(() =>
    auth.currentUser?.providerData?.map((p) => p.providerId) || []
  );
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordLink, setShowPasswordLink] = useState(false);
  const [linkEmail, setLinkEmail] = useState(auth.currentUser?.email || '');
  const [linkPassword, setLinkPassword] = useState('');

  const refresh = () => {
    setLinked(auth.currentUser?.providerData?.map((p) => p.providerId) || []);
  };

  const handleLinkOAuth = async (provider) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await linkWithPopup(auth.currentUser, provider.factory());
      setSuccess(`${provider.label} linked successfully.`);
      refresh();
    } catch (err) {
      if (err.code === 'auth/credential-already-in-use') {
        setError(`This ${provider.label} account is already linked to a different mChatAI account. Use Account Merge to combine them.`);
      } else if (err.code === 'auth/provider-already-linked') {
        setError(`${provider.label} is already linked.`);
        refresh();
      } else if (err.code !== 'auth/popup-closed-by-user') {
        setError(`${err.code}: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkPassword = async (e) => {
    e.preventDefault();
    if (!linkEmail.trim() || !linkPassword.trim()) return;
    if (linkPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const credential = EmailAuthProvider.credential(linkEmail.trim(), linkPassword);
      await linkWithCredential(auth.currentUser, credential);
      setSuccess('Email/Password linked. You can now sign in with this on any platform.');
      setShowPasswordLink(false);
      setLinkPassword('');
      refresh();
    } catch (err) {
      if (err.code === 'auth/credential-already-in-use') {
        setError('This email is already linked to a different mChatAI account. Use Account Merge to combine them.');
      } else if (err.code === 'auth/provider-already-linked') {
        setError('A password is already linked to this account.');
        refresh();
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (providerId) => {
    if (linked.length <= 1) {
      setError('You must have at least one sign-in method.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await unlink(auth.currentUser, providerId);
      setSuccess('Provider unlinked.');
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasPassword = linked.includes('password');
  const currentEmail = auth.currentUser?.email || '';
  const isRelayEmail = currentEmail.includes('@privaterelay.apple.com');

  return (
    <div style={card}>
      <div style={{ color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 16 }}>
        Linked Sign-In Methods
      </div>

      {/* Currently linked providers */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        {linked.map((pid) => {
          const meta = PROVIDERS.find((p) => p.id === pid);
          const label = pid === 'password' ? 'Email/Password' : meta?.label || pid;
          const providerEmail = auth.currentUser?.providerData?.find(p => p.providerId === pid)?.email;
          return (
            <div key={pid} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {pid === 'password' ? <PasswordIcon /> : meta?.icon || <span style={{ width: 16 }} />}
                <div>
                  <span style={{ color: '#111827', fontSize: '0.85rem', fontWeight: 500 }}>{label}</span>
                  {providerEmail && (
                    <span style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: 8 }}>{providerEmail}</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleUnlink(pid)}
                disabled={loading || linked.length <= 1}
                style={{
                  background: 'none', border: 'none',
                  color: linked.length <= 1 ? '#d1d5db' : '#6b7280',
                  fontSize: '0.75rem', cursor: linked.length <= 1 ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Unlink
              </button>
            </div>
          );
        })}
      </div>

      {/* Link new providers */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {PROVIDERS.filter((p) => !linked.includes(p.id)).map((provider) => (
          <button
            key={provider.id}
            onClick={() => handleLinkOAuth(provider)}
            disabled={loading}
            style={{ ...linkBtnStyle, opacity: loading ? 0.5 : 1 }}
          >
            {provider.icon}
            <span>Link {provider.label}</span>
          </button>
        ))}
        {!hasPassword && !showPasswordLink && (
          <button
            onClick={() => { setLinkEmail(isRelayEmail ? '' : currentEmail); setShowPasswordLink(true); }}
            disabled={loading}
            style={linkBtnStyle}
          >
            <PasswordIcon />
            <span>Set Password</span>
          </button>
        )}
      </div>

      {/* Password link form */}
      {showPasswordLink && (
        <form onSubmit={handleLinkPassword} style={{
          marginTop: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16,
        }}>
          {isRelayEmail && (
            <p style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem', margin: '0 0 12px' }}>
              Your Apple ID uses a private relay email. Enter a real email you can use to sign in on other devices.
            </p>
          )}
          <input
            type="email"
            placeholder="Email for cross-platform sign-in"
            value={linkEmail}
            onChange={(e) => setLinkEmail(e.target.value)}
            required
            style={formInputStyle}
          />
          <input
            type="password"
            placeholder="New password (6+ characters)"
            value={linkPassword}
            onChange={(e) => setLinkPassword(e.target.value)}
            required
            minLength={6}
            style={{ ...formInputStyle, marginTop: 8 }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setShowPasswordLink(false)}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.8rem' }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem' }}>
              {loading ? 'Linking…' : 'Link Password'}
            </button>
          </div>
        </form>
      )}

      {error && (
        <p style={{ color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem', margin: '12px 0 0' }}>
          {error}
        </p>
      )}
      {success && (
        <p style={{ color: '#166534', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem', margin: '12px 0 0' }}>
          {success}
        </p>
      )}
    </div>
  );
}

const linkBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db',
  background: '#fff', color: '#374151', fontSize: '0.8rem',
  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
};

const formInputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #d1d5db', background: '#fff', color: '#111827',
  fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

function GoogleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 17 17" fill="currentColor">
      <path d="M12.15 8.48c-.02-2.01 1.64-2.98 1.71-3.02-.93-1.36-2.38-1.55-2.9-1.57-1.23-.12-2.4.72-3.03.72-.63 0-1.6-.71-2.63-.69-1.35.02-2.6.79-3.3 2-.84 1.45-.64 4.17.66 5.83.44.63.96 1.35 1.65 1.32.66-.03.91-.43 1.71-.43s1.02.43 1.72.41c.71-.01 1.16-.65 1.6-1.28.5-.73.71-1.44.72-1.48-.02-.01-1.38-.53-1.4-2.1z"/>
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

function PasswordIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}
